/**
 * Which tabs of an event's page are shown. One pure rule, used by the dashboard to draw the page
 * and by a unit test (test:event-page) that walks every kind against every tab. No imports: the
 * frontend builds this folder alone.
 *
 * A tab is shown when any of these holds:
 *   - it is pinned (always useful: attendance, and the notes for a light event);
 *   - something is stored behind it (HAS_DATA), so a wrong guess about a kind never hides real data;
 *   - the event is a full one that has not ended yet (planning needs every tab);
 *   - the viewer asked for all tabs.
 */

/** Counts of what exists for one event, from GET /api/events/:id/content (never names). */
export interface EventContent {
  invitees: number;
  checkins: number;
  attendance: number;
  attendanceNumbers: number;
  sessions: number;
  recordings: number;
  speakers: number;
  tickets: number;
  budgetLines: number;
  vendors: number;
  giftCards: number;
  finance: number;
  notes: number;
  responses: number;
  ledger: number;
  reimbursements: number;
  checklist: number;
  pheedloop: number;
  zoom: number;
  hotel: number;
  travel: number;
  closed: boolean;
  configured: { tickets: boolean; outreach: boolean; hotel: boolean; dayOf: boolean; comms: boolean; program: boolean };
}

/** The tabs of each stage; the frontend's tab list is typed against these keys. */
export const EVENT_TABS = {
  before: [
    'overview',
    'followups',
    'people',
    'registration',
    'speakers',
    'hotel',
    'catering',
    'logistics',
    'budget',
    'emails',
    'timeline',
    'documents',
  ],
  during: ['day'],
  after: ['attendance', 'feedback', 'recordings', 'report', 'reimbursements', 'honoraria', 'budget', 'next_year'],
} as const;

export type EventStage = keyof typeof EVENT_TABS;
export type EventTabKey<S extends EventStage = EventStage> = (typeof EVENT_TABS)[S][number];

/**
 * full: an event DIVERT organises (people, tickets, travel, money): every tab while it is ahead.
 * light: an event that is mostly a headcount and notes: only the pinned tabs, plus any with data.
 * An unknown kind counts as full, so nothing is ever hidden for lack of a rule.
 * Outreach talks have their own page and never come through here.
 */
export type EventShape = 'full' | 'light';
export const EVENT_SHAPE: Record<string, EventShape> = {
  national: 'full',
  regional: 'light',
  pi_meeting: 'light',
};
export const shapeOf = (kind: string | null | undefined): EventShape => EVENT_SHAPE[kind ?? ''] ?? 'full';

const PINNED: Record<EventShape, string[]> = {
  full: [],
  light: [],
};

const money = (c: EventContent) => c.budgetLines + c.vendors + c.giftCards + c.finance > 0;

/** What counts as "something is stored behind this tab". A tab not listed has no data test. */
export const HAS_DATA: Record<string, (c: EventContent, ended: boolean) => boolean> = {
  'before.overview': (c) => c.invitees + c.sessions + c.tickets > 0,
  'before.followups': (c) => c.invitees > 0,
  'before.people': (c) => c.invitees + c.tickets > 0,
  'before.registration': (c) => c.invitees + c.tickets > 0 || c.configured.tickets || c.configured.outreach,
  'before.speakers': (c) => c.sessions + c.speakers > 0 || c.configured.program,
  'before.hotel': (c) => c.hotel > 0 || c.configured.hotel,
  'before.catering': (c) => c.invitees > 0,
  'before.logistics': (c) => c.travel > 0 || c.configured.dayOf,
  'before.budget': money,
  'before.emails': (c) => c.invitees > 0 || c.configured.comms,
  'before.timeline': (c) => c.checklist + c.vendors > 0,
  'before.documents': (c) => c.invitees + c.sessions + c.pheedloop > 0,
  'during.day': (c) => c.invitees + c.checkins + c.sessions > 0,
  'after.attendance': (c, ended) =>
    (ended && c.invitees > 0 && !c.closed) || c.zoom + c.pheedloop + c.attendanceNumbers > 0,
  'after.feedback': (c) => c.responses > 0,
  'after.recordings': (c) => c.recordings > 0,
  'after.report': (c, ended) => (ended ? c.invitees : 0) + c.attendance + c.checkins + c.responses + c.zoom > 0,
  'after.reimbursements': (c) => c.reimbursements > 0,
  'after.honoraria': (c) => c.ledger + c.giftCards > 0,
  'after.budget': money,
  'after.next_year': (c) => c.notes > 0,
};

export const EVENT_AREAS: string[] = (Object.keys(EVENT_TABS) as EventStage[]).flatMap((s) =>
  EVENT_TABS[s].map((t) => `${s}.${t}`),
);

/** Is this tab shown? `content` not yet loaded shows everything, so the page never flashes tabs away. */
export function areaVisible(
  kind: string | null | undefined,
  area: string,
  content: EventContent | null | undefined,
  { ended, showAll = false }: { ended: boolean; showAll?: boolean },
): boolean {
  if (showAll || !content) return true;
  if (area === 'before.budget' && ended) return false;
  if (area === 'after.budget' && !ended) return false;
  if (PINNED[shapeOf(kind)].includes(area)) return true;
  if (HAS_DATA[area]?.(content, ended)) return true;
  return shapeOf(kind) === 'full' && !ended && !area.startsWith('after.');
}

/**
 * Panels inside a tab that need something stored behind them. An event that is ahead shows them all if it is a full
 * one (planning needs them); once it is over, and always for a light event, a panel shows only when its rule says
 * something is there (or, for the Zoom report, when the event is hybrid). Panels not listed here show wherever their
 * tab does. `before.speakers.import` is the one that shows when the agenda is still empty.
 */
const tickets = (c: EventContent) => c.tickets > 0 || c.configured.tickets;
const outreach = (c: EventContent) => c.configured.tickets || c.configured.outreach;
export const PANEL_DATA: Record<string, (c: EventContent, event: { hybrid: boolean; ended: boolean }) => boolean> = {
  'after.attendance.close': (c) => c.invitees > 0 && !c.closed,
  'after.attendance.zoom': (c, e) => c.zoom > 0 || (e.hybrid && !e.ended),
  'after.attendance.pheedloop': (c) => c.pheedloop > 0,
  'after.feedback.sessions': (c) => c.sessions > 0,
  'after.report.summary': (c) => c.invitees + c.attendance + c.checkins > 0,
  'after.honoraria.steps': (c) => c.ledger > 0,
  'after.honoraria.giftcards': (c) => c.giftCards > 0,
  'after.reimbursements.steps': (c) => c.reimbursements > 0,
  'after.budget.vendors': (c) => c.vendors > 0,
  'after.budget.treasury': (c) => c.finance > 0,
  'before.documents.pheedloop': (c) => c.pheedloop > 0,
  'before.budget.vendors': (c) => c.vendors > 0,
  'before.budget.giftcards': (c) => c.giftCards > 0,
  'before.budget.treasury': (c) => c.finance > 0,
  'before.logistics.staff': (c) => c.configured.dayOf,
  'before.logistics.groups': (c) => c.configured.dayOf,
  'before.logistics.parking': (c) => c.configured.dayOf,
  'before.speakers.import': (c) => c.sessions === 0,
  'before.speakers.interpretation': (c) => c.configured.program,
  'before.speakers.dignitaries': (c) => c.configured.program,
  'before.speakers.competitions': (c) => c.configured.program,
  'before.registration.tickets': tickets,
  'before.registration.outreach': (c) => c.configured.outreach,
  'before.registration.calendar': outreach,
  'before.registration.comps': outreach,
  'before.registration.partners': outreach,
};

export function panelVisible(
  kind: string | null | undefined,
  panel: string,
  content: EventContent | null | undefined,
  { showAll = false, hybrid = false, ended = false }: { showAll?: boolean; hybrid?: boolean; ended?: boolean } = {},
): boolean {
  if (showAll || !content) return true;
  const rule = PANEL_DATA[panel];
  if (!rule) return true;
  if (shapeOf(kind) === 'full' && !ended) return true;
  return rule(content, { hybrid, ended });
}
